import { useEffect, useState } from 'react'
import { toPanelError } from '@/console/api/errors'
import { RepublishResponse } from '@/console/api/types'
import { requestRepublish } from '@/console/data/republish'
import { PanelState, loading, ready } from '@/console/state/panelState'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { ConfirmDialog } from '@/console/ui/ConfirmDialog'
import { DataTable } from '@/console/ui/DataTable'
import { Field } from '@/console/ui/Field'
import { Note } from '@/console/ui/Note'
import { ResultState } from '@/console/ui/ResultState'
import { SectionHeading } from '@/console/ui/SectionHeading'
import { SourceBadge } from '@/console/ui/SourceBadge'
import { Stack } from '@/console/ui/Stack'
import { Tag } from '@/console/ui/Tag'
import { MAX_REPUBLISH_IDS, parseInboundReportIds, republishOutcomeTone, summarizeRepublish, validateReason } from './republish'

/**
 * LOGISPRD-13362 — MASS `반품 정산 재발행`(`s-mass-republish`) + `재발행 결과`(`s-mass-result`).
 *
 * 핸드오프 §4.6 함정: 서버가 클레임번호 중복을 검사하지 않아, 이미 발행된 건을 다시 보내면
 * 이중청구가 될 수 있다 — 확인 다이얼로그에 그 문구를 반드시 넣는다. `inboundReportIds` 는
 * 범위 조회가 아니라 ID 직접 입력만 받는다(이중청구 위험으로 만들지 않는다).
 *
 * 숫자가 아닌 값이 하나라도 섞이면 제출을 막는다 — F-OPT 재발사(`RedispatchPanel`)의 PR 리뷰 결론과
 * 같다. 유효한 값만 골라 보내면 담당자는 전부 재발행됐다고 믿지만 일부만 나간다.
 */
export const RepublishPanel = ({ onBusyChange }: { onBusyChange?: (busy: boolean) => void }): JSX.Element => {
  const [raw, setRaw] = useState('')
  const [reason, setReason] = useState('')
  const [pendingConfirm, setPendingConfirm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<RepublishResponse | null>(null)
  const [error, setError] = useState<{ kind: 'forbidden' | 'other'; message: string } | null>(null)

  // `FoptTab` 과 같은 모양 — 언마운트 시 false 로 돌려 셸의 탭바가 잠긴 채 남지 않게 한다.
  useEffect(() => {
    onBusyChange?.(submitting)
    return () => onBusyChange?.(false)
  }, [submitting, onBusyChange])

  const parsed = parseInboundReportIds(raw)
  const reasonError = validateReason(reason)
  const canSubmit = parsed.ids.length > 0 && parsed.invalid.length === 0 && !parsed.overLimit && !reasonError && !submitting

  const resultState: PanelState<RepublishResponse> | null = submitting
    ? loading()
    : error
      ? { status: 'error', ...error }
      : result
        ? ready(result)
        : null

  const confirmRepublish = (): void => {
    setPendingConfirm(false)
    setSubmitting(true)
    setError(null)

    requestRepublish({ inboundReportIds: parsed.ids, reason: reason.trim() })
      .then((response) => {
        setResult(response)
      })
      .catch((err) => {
        setResult(null)
        setError(toPanelError(err))
      })
      .finally(() => setSubmitting(false))
  }

  const summary = result ? summarizeRepublish(result) : []

  return (
    <>
      <SectionHeading
        id="s-mass-republish"
        level={2}
        title="반품 정산 재발행"
        badge={<SourceBadge tone="have">기존 · POST /settlement/return-inbound/republish</SourceBadge>}
      />

      <Stack>
        <Field label="inboundReportIds — UI_GRREPORT_DETAIL.id (쉼표 · 줄바꿈)">
          <textarea
            rows={4}
            placeholder="9931204, 9931207"
            spellCheck={false}
            value={raw}
            disabled={submitting}
            onChange={(e) => setRaw(e.target.value)}
          />
        </Field>

        <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
          유효 {parsed.ids.length}건 · 중복 제외 {parsed.duplicates}건 · 최대 {MAX_REPUBLISH_IDS}건
        </div>

        {parsed.overLimit ? <Callout tone="crit">한 번에 최대 {MAX_REPUBLISH_IDS}건까지 보낼 수 있어요.</Callout> : null}
        {parsed.invalid.length > 0 ? <Callout tone="warn">숫자가 아닌 값이 있어요: {parsed.invalid.join(', ')}</Callout> : null}

        <Field label="사유 (감사 로그에 남아요 — 티켓키 권장)">
          <input type="text" spellCheck={false} placeholder="LOGISPRD-11517 8/4·8/12 미발행분" value={reason} disabled={submitting} onChange={(e) => setReason(e.target.value)} />
        </Field>

        {reasonError && reason.length > 0 ? <Callout tone="crit">{reasonError}</Callout> : null}

        <div>
          <Button disabled={!canSubmit} onClick={() => setPendingConfirm(true)}>
            {submitting ? '재발행 중…' : '재발행'}
          </Button>
        </div>
      </Stack>

      <Note>
        ID 직접 입력만 — 범위 조회는 이중청구 위험으로 만들지 않아요. 보내기 전에 MASS 에 이미 정산된 건이 아닌지 먼저 확인해요. <b>MOMS_ENG</b> 권한 필요.
      </Note>

      <SectionHeading id="s-mass-result" level={2} title="재발행 결과" />
      {resultState ? (
        <ResultState state={resultState}>
          {result ? (
            <Stack>
              <DataTable
                columns={summary.map((row) => ({ key: row.key, label: row.label, align: 'right' as const }))}
                rows={[
                  {
                    key: 'summary',
                    cells: Object.fromEntries(summary.map((row) => [row.key, <Tag key={row.key} tone={row.value === 0 ? 'neutral' : row.tone}>{row.value}건</Tag>])),
                  },
                ]}
              />
              <DataTable
                maxHeight={480}
                stickyHeader
                columns={[
                  { key: 'id', label: 'inboundReportId', mono: true },
                  { key: 'outcome', label: '결과' },
                  { key: 'detail', label: '상세' },
                ]}
                rows={result.items.map((item) => ({
                  key: String(item.inboundReportId),
                  cells: {
                    id: item.inboundReportId,
                    outcome: <Tag tone={republishOutcomeTone(item.outcome)}>{item.outcome}</Tag>,
                    detail: item.detail ?? '-',
                  },
                }))}
              />
            </Stack>
          ) : null}
        </ResultState>
      ) : null}
      <Note>
        <b>PUBLISHED</b> 는 Kafka 로 보냈다는 뜻이지 MASS 가 받았다는 확인이 아니에요. 과거 입고일 건이면 그 날짜의 일마감 정산을 따로 다시 돌려야 해요. 자동 재시도는 하지 않아요.
      </Note>

      <ConfirmDialog
        open={pendingConfirm}
        title="재발행할까요?"
        body={
          <>
            <p style={{ margin: 0 }}>
              {parsed.ids.length}건 · 사유: {reason.trim()}
            </p>
            <ul style={{ margin: '10px 0 0', paddingLeft: 18 }}>
              <li>서버는 클레임번호 중복을 검사하지 않아요. 이미 정산된 건을 다시 보내면 이중청구가 될 수 있어요.</li>
              <li>MASS 에서 이미 정산된 건이 아닌지 먼저 확인했나요?</li>
              <li>과거 입고일 건이면 재발행 뒤 그 날짜의 일마감 정산을 따로 다시 돌려야 해요.</li>
            </ul>
          </>
        }
        confirmLabel="재발행"
        danger
        onConfirm={confirmRepublish}
        onCancel={() => setPendingConfirm(false)}
      />
    </>
  )
}
